import React, { useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../context/AuthContext";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, AlertCircle, RefreshCw,
  Mail, CreditCard, ShieldCheck, ChevronDown, ChevronUp,
} from "lucide-react";

const StatusPill = ({ ok, warning, label }) => {
  const cls = ok
    ? "bg-green-100 text-green-700 border-green-200"
    : warning
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-red-100 text-red-700 border-red-200";
  const Icon = ok ? CheckCircle2 : warning ? AlertTriangle : AlertCircle;
  return (
    <span
      data-testid={`config-status-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${cls}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const Row = ({ label, ok, warning, hint }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <div>
      <div className="text-sm font-medium text-[#0A192F]">{label}</div>
      {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
    </div>
    <StatusPill ok={ok} warning={warning} label={ok ? "OK" : warning ? "missing" : "missing"} />
  </div>
);

export default function ProductionConfigWidget() {
  const { user } = useAuth();
  const role = user?.role;
  const allowed = role === "owner" || role === "admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [showChecklist, setShowChecklist] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/config/status");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        // silently hide widget for users without permission
        setData(null);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (!allowed) return null; // server-side already enforces, but hide UI too
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 p-6 mt-6" data-testid="production-config-widget-loading">
        <div className="text-sm text-gray-500">Productieconfig laden…</div>
      </div>
    );
  }
  if (!data) return null;

  const r = data.resend || {};
  const s = data.stripe || {};
  const resendOk = r.api_key?.configured && r.from_email_set;
  const stripeBaseOk = s.secret_key?.configured && s.webhook_secret?.configured;
  const stripeUrlsOk = s.success_url_set && s.cancel_url_set;
  const liveMode = s.mode === "live";
  const ready = data.ready_for_production;

  return (
    <div className="bg-white border border-gray-200 mt-6" data-testid="production-config-widget">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50"
        data-testid="production-config-toggle"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className={`w-5 h-5 ${ready ? "text-green-600" : "text-amber-600"}`} />
          <div>
            <div className="font-chivo font-black text-[#0A192F]">Productieconfiguratie</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Status van Resend + Stripe — geen secrets zichtbaar.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill ok={ready} warning={!ready && (resendOk || stripeBaseOk)}
                       label={ready ? "ready" : "needs-config"} />
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-200 p-5 space-y-6">
          {/* Resend */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-[#0055FF]" />
              <h3 className="font-chivo font-black uppercase text-xs tracking-wider">Resend (e-mail)</h3>
            </div>
            <div className="text-xs text-gray-600 mb-2">
              {r.api_key?.configured ? (
                <>API key: <code className="font-mono">{r.api_key.preview}</code> ({r.api_key.length} chars)</>
              ) : (
                <>Nog geen <code>RESEND_API_KEY</code> ingesteld — e-mails worden gelogd als <code>skipped_no_key</code>.</>
              )}
            </div>
            <Row label="API key" ok={!!r.api_key?.configured} warning={false}
                 hint="RESEND_API_KEY in backend/.env" />
            <Row label="From e-mail"
                 ok={!!r.from_email_set && r.domain_hint && r.domain_hint !== "resend.dev"}
                 warning={!!r.from_email_set && r.domain_hint === "resend.dev"}
                 hint={r.from_email_value ? `Huidig: ${r.from_email_value}` : "FROM_EMAIL ontbreekt"} />
            <Row label="Admin notificatie e-mail" ok={!!r.admin_notification_email_set}
                 hint={r.admin_notification_email_value || "ADMIN_NOTIFICATION_EMAIL ontbreekt"} />
          </section>

          {/* Stripe */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-[#0055FF]" />
              <h3 className="font-chivo font-black uppercase text-xs tracking-wider">Stripe (betalingen)</h3>
              <span
                className={`ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${
                  liveMode
                    ? "bg-red-50 text-red-700 border-red-200"
                    : s.mode === "test"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-gray-50 text-gray-700 border-gray-200"
                }`}
                data-testid="stripe-mode-badge"
              >
                Mode: {s.mode || "missing"}
              </span>
            </div>
            <div className="text-xs text-gray-600 mb-2">
              {s.secret_key?.configured ? (
                <>Secret key: <code className="font-mono">{s.secret_key.preview}</code></>
              ) : (
                <>Nog geen <code>STRIPE_SECRET_KEY</code> ingesteld.</>
              )}
            </div>
            <Row label="Secret key" ok={!!s.secret_key?.configured}
                 hint="STRIPE_SECRET_KEY in backend/.env" />
            <Row label="Webhook secret" ok={!!s.webhook_secret?.configured}
                 hint="STRIPE_WEBHOOK_SECRET — vereist voor /api/stripe/webhook" />
            <Row label="Success URL" ok={!!s.success_url_set}
                 hint={s.success_url_value || "STRIPE_SUCCESS_URL fallt back to {origin}/webshop/success"} />
            <Row label="Cancel URL" ok={!!s.cancel_url_set}
                 hint={s.cancel_url_value || "STRIPE_CANCEL_URL fallt back to {origin}/webshop/cancel"} />
            {liveMode && s.needs_resync && (
              <div className="mt-3 bg-red-50 border border-red-200 p-3 text-xs text-red-800" data-testid="stripe-live-warning">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                <strong>Live mode actief —</strong> {s.mismatched_mode_products + s.mismatched_mode_variations + s.legacy_unknown_mode_products + s.legacy_unknown_mode_variations} producten/varianten zijn nog niet met live Stripe IDs gesynchroniseerd. Run Webshopbeheer → Stripe → "Sync alles met Stripe" om opnieuw te synchroniseren.
              </div>
            )}
            {liveMode && !s.needs_resync && (
              <div className="mt-3 bg-green-50 border border-green-200 p-3 text-xs text-green-800" data-testid="stripe-live-ok">
                <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
                <strong>Live mode actief —</strong> alle producten zijn met live Stripe IDs gesynchroniseerd. Betalingen worden live verwerkt.
              </div>
            )}
          </section>

          {/* Activity */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div className="border border-gray-200 p-3">
              <div className="uppercase text-gray-500 font-bold mb-1">Laatste e-mail</div>
              <div className="text-gray-700">
                {data.last_email
                  ? `${data.last_email.type}: ${data.last_email.status}`
                  : "Nog geen e-mail verzonden"}
              </div>
            </div>
            <div className="border border-gray-200 p-3">
              <div className="uppercase text-gray-500 font-bold mb-1">Laatste webhook</div>
              <div className="text-gray-700">
                {data.last_webhook_event
                  ? `${data.last_webhook_event.type} (${data.last_webhook_event.processed ? "verwerkt" : "wacht"})`
                  : "Geen webhook events"}
              </div>
            </div>
            <div className="border border-gray-200 p-3">
              <div className="uppercase text-gray-500 font-bold mb-1">Laatste sync</div>
              <div className="text-gray-700">
                {data.last_stripe_sync
                  ? `${data.last_stripe_sync.extra?.synced ?? 0} synced, ${data.last_stripe_sync.extra?.errors ?? 0} fouten`
                  : "Nog niet gesynchroniseerd"}
              </div>
            </div>
          </section>

          {/* Actions */}
          <section className="flex flex-wrap gap-3 items-center">
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-[#0055FF] hover:underline"
              data-testid="production-config-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Vernieuwen
            </button>
            <button
              type="button"
              onClick={() => setShowChecklist((v) => !v)}
              className="text-xs uppercase tracking-wider font-bold text-[#0A192F] hover:underline"
              data-testid="production-config-checklist-toggle"
            >
              {showChecklist ? "Verberg checklists" : "Toon checklists"}
            </button>
          </section>

          {showChecklist && (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs" data-testid="production-config-checklists">
              <div className="bg-[#F8F9FA] border border-gray-200 p-4">
                <h4 className="font-chivo font-black uppercase text-xs mb-2">Resend productie checklist</h4>
                <ol className="list-decimal list-inside space-y-1 text-gray-700">
                  <li>Voeg <code>5999cargo.nl</code> toe in Resend → Domains.</li>
                  <li>Kopieer SPF + DKIM DNS records uit Resend.</li>
                  <li>Voeg DNS records toe bij de DNS-provider.</li>
                  <li>Wacht op verified status (kan 5-30 min duren).</li>
                  <li>Vul <code>RESEND_API_KEY</code> in backend/.env.</li>
                  <li>Vul <code>FROM_EMAIL="5999Cargo &lt;noreply@5999cargo.nl&gt;"</code> in.</li>
                  <li>Test contactformulier (<code>POST /api/contact</code>).</li>
                  <li>Test orderbevestiging via "Resend e-mail" in Bestellingen.</li>
                  <li>Controleer email_log: status="sent".</li>
                </ol>
              </div>
              <div className="bg-[#F8F9FA] border border-gray-200 p-4">
                <h4 className="font-chivo font-black uppercase text-xs mb-2">Stripe LIVE checklist</h4>
                <ol className="list-decimal list-inside space-y-1 text-gray-700">
                  <li>Activeer Stripe live account in Dashboard.</li>
                  <li>Vul live <code>STRIPE_SECRET_KEY</code> (sk_live_…) in backend/.env.</li>
                  <li>Vul live <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> (pk_live_…) in frontend/.env.</li>
                  <li>Maak live webhook endpoint:<br />
                    <code>https://&lt;domein&gt;/api/stripe/webhook</code></li>
                  <li>Selecteer event <code>checkout.session.completed</code>.</li>
                  <li>Kopieer signing secret → <code>STRIPE_WEBHOOK_SECRET</code>.</li>
                  <li>Set <code>STRIPE_SUCCESS_URL</code> + <code>STRIPE_CANCEL_URL</code> naar productiedomein.</li>
                  <li>Webshopbeheer → Stripe → "Sync alles met Stripe" (live IDs).</li>
                  <li>Doe €1 testbetaling met echte kaart, controleer order op paid.</li>
                </ol>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
